import ReactECharts from "echarts-for-react";
import { useState, useEffect } from "react";
import axios from "axios";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Generale.css";
import Lf23Loader from "../../../components/Lf23Loader";
import KmPerImmatricolazione from "./KmPerImmatricolazione";

const AutomezziCharts = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [tipologieDataFromApi, setTipologieData] = useState([]);
    const [modelliDataFromApi, setModelliData] = useState([]);

    // dati del grafico combo - contengono sempre tutti i veicoli
    const [comboDataTrimestraleFromApi, setComboDataTrimestrale] = useState([]);
    const [comboDataMensileFromApi, setComboDataMensile] = useState([]);
    const [comboDataAnnualeFromApi, setComboDataAnnuale] = useState([]);

    // dati del grafico combo filtrati per tipologia (quando si clicca sulla torta)
    const [comboDataTrimestraleFiltered, setComboDataTrimestraleFiltered] = useState([]);
    const [comboDataMensileFiltered, setComboDataMensileFiltered] = useState([]);
    const [comboDataAnnualeFiltered, setComboDataAnnualeFiltered] = useState([]);

    // vista del grafico combo: mensile, trimestrale o annuale
    const [comboViewType, setComboViewType] = useState('trimestre');

    // tipologia selezionata cliccando sulla torta (es. "Porter", "Compattatore")
    const [selectedTipologia, setSelectedTipologia] = useState(null);

    // Loading locale per i fetch dei dati
    const [dataLoading, setDataLoading] = useState(false);

    // dati istogrammi anzianità veicoli
    const [anzianitaData, setAnzianitaData] = useState([]);
    const [anzianitaMediaData, setAnzianitaMediaData] = useState([]);

    // stati fullscreen
    const [isFullscreenPie, setIsFullscreenPie] = useState(false);
    const [isFullscreenModelli, setIsFullscreenModelli] = useState(false);
    const [isFullscreenCombo, setIsFullscreenCombo] = useState(false);
    const [isFullscreenAnzianita, setIsFullscreenAnzianita] = useState(false);
    const [isFullscreenAnzianitaMedia, setIsFullscreenAnzianitaMedia] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenPie(false);
                setIsFullscreenModelli(false);
                setIsFullscreenCombo(false);
                setIsFullscreenAnzianita(false);
                setIsFullscreenAnzianitaMedia(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenPie, isFullscreenModelli, isFullscreenCombo]);

    // converte i filtri selezionati dall'utente in parametri per le chiamate API
    const buildFilterParams = ({ includeDates = true } = {}) => {
        const params = new URLSearchParams();
        if (includeDates && filters?.fromDate) {
            params.append('dataInizio', filters.fromDate);
        }
        if (includeDates && filters?.toDate) {
            params.append('dataFine', filters.toDate);
        }
        if (filters?.tipologiaVeicolo?.length > 0) {
            params.append('tipologia', filters.tipologiaVeicolo.map(t => t.value).join(','));
        }
        if (filters?.targa?.length > 0) {
            params.append('targa', filters.targa.map(t => t.value).join(','));
        }
        if (filters?.cantiere?.length > 0) {
            params.append('cantiere', filters.cantiere.map(c => c.value).join(','));
        }
        if (filters?.settore?.length > 0) {
            params.append('settore', filters.settore.map(s => s.value).join(','));
        }
        if (filters?.commessa?.length > 0) {
            params.append('commessa', filters.commessa.map(c => c.value).join(','));
        }
        if (filters?.operatore?.length > 0) {
            params.append('operatore', filters.operatore.map(o => o.value).join(','));
        }

        return params.toString();
    };

    // carico i dati per i grafici a torta e a barre quando l'utente applica i filtri
    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                const filterParams = buildFilterParams();
                const queryString = filterParams ? `?${filterParams}` : '';

                const tipologieRes = await axios.get(`${API_URL}/operation-automezzi/generale/tipologie${queryString}`);
                setTipologieData(tipologieRes.data);

                const modelliRes = await axios.get(`${API_URL}/operation-automezzi/generale/modelli${queryString}`);
                setModelliData(modelliRes.data);

                // Quando cambiano i filtri, resetto
                setSelectedTipologia(null);

            } catch (error) {
                console.error("Errore durante il caricamento dei dati:", error);
            } finally {
                setDataLoading(false);
            }
        };

        fetchData();
    }, [API_URL, filters]);

    // Fetch anzianità veicoli (slegato dal filtro temporale, risponde agli altri filtri)
    useEffect(() => {
        const fetchAnzianita = async () => {
            try {
                const filterParams = buildFilterParams({ includeDates: false });
                const queryString = filterParams ? `?${filterParams}` : '';
                const [res1, res2] = await Promise.all([
                    axios.get(`${API_URL}/operation-automezzi/generale/anzianita-veicoli${queryString}`),
                    axios.get(`${API_URL}/operation-automezzi/generale/anzianita-media-sottotipologia${queryString}`)
                ]);
                setAnzianitaData(res1.data);
                setAnzianitaMediaData(res2.data);
            } catch (error) {
                console.error("Errore nel caricamento dati anzianità veicoli:", error);
            }
        };
        fetchAnzianita();
    }, [API_URL, filters]);

    // anno selezionato per la vista mensile
    const selectedYear = filters?.fromDate
        ? new Date(filters.fromDate).getFullYear()
        : 2025;

    // carico i dati generali del grafico combo (tutti i veicoli, senza distinzione per tipologia)
    useEffect(() => {
        const fetchComboDataTotal = async () => {
            try {
                const filterParams = buildFilterParams();
                const queryString = filterParams ? `?${filterParams}` : '';

                if (comboViewType === 'trimestre') {
                    const res = await axios.get(`${API_URL}/operation-automezzi/generale/targhe-circolanti${queryString}`);
                    setComboDataTrimestrale(res.data);
                } else if (comboViewType === 'mese') {
                    const res = await axios.get(`${API_URL}/operation-automezzi/generale/targhe-mensili${queryString}`);
                    setComboDataMensile(res.data);
                } else if (comboViewType === 'anno') {
                    const res = await axios.get(`${API_URL}/operation-automezzi/generale/targhe-annuali${queryString}`);
                    setComboDataAnnuale(res.data);
                }
            } catch (error) {
                console.error("Errore nel caricamento dati combo totali:", error);
            }
        };

        fetchComboDataTotal();
    }, [comboViewType, selectedYear, filters, API_URL]);

    // carico i dati del grafico combo filtrati per la tipologia selezionata (quando si clicca sulla torta)
    useEffect(() => {
        const fetchComboDataFiltered = async () => {
            if (!selectedTipologia) {
                // Nessuna tipologia selezionata, svuoto i dati filtrati
                setComboDataTrimestraleFiltered([]);
                setComboDataMensileFiltered([]);
                setComboDataAnnualeFiltered([]);
                return;
            }

            try {
                const tipologiaParam = `tipologia=${encodeURIComponent(selectedTipologia)}`;
                const filterParams = buildFilterParams();

                if (comboViewType === 'trimestre') {
                    const queryString = filterParams ? `?${filterParams}&${tipologiaParam}` : `?${tipologiaParam}`;
                    const res = await axios.get(`${API_URL}/operation-automezzi/generale/targhe-circolanti${queryString}`);
                    setComboDataTrimestraleFiltered(res.data);
                } else if (comboViewType === 'mese') {
                    const queryString = filterParams ? `?${filterParams}&${tipologiaParam}` : `?${tipologiaParam}`;
                    const res = await axios.get(`${API_URL}/operation-automezzi/generale/targhe-mensili${queryString}`);
                    setComboDataMensileFiltered(res.data);
                } else if (comboViewType === 'anno') {
                    const queryString = filterParams ? `?${filterParams}&${tipologiaParam}` : `?${tipologiaParam}`;
                    const res = await axios.get(`${API_URL}/operation-automezzi/generale/targhe-annuali${queryString}`);
                    setComboDataAnnualeFiltered(res.data);
                }
            } catch (error) {
                console.error("Errore nel caricamento dati combo filtrati:", error);
            }
        };

        fetchComboDataFiltered();
    }, [comboViewType, selectedYear, selectedTipologia, filters, API_URL]);

    // Validazione formato dati
    const hasValidDataFormat =
        tipologieDataFromApi !== null && Array.isArray(tipologieDataFromApi) &&
        modelliDataFromApi !== null && Array.isArray(modelliDataFromApi);

    // Combina loading del padre con validazioni locali
    const isLoading = parentLoading || dataLoading || !hasValidDataFormat;
    const isDataReady = !parentLoading && !dataLoading && hasValidDataFormat &&
        tipologieDataFromApi.length > 0 && modelliDataFromApi.length > 0;
    const isEmpty = !parentLoading && !dataLoading && hasValidDataFormat &&
        (tipologieDataFromApi.length === 0 || modelliDataFromApi.length === 0);


    // Quando si clicca su una tipologia, evidenzio quella fetta e sfumo le altre
    const tipologieData = tipologieDataFromApi.map(item => ({
        ...item,
        itemStyle: selectedTipologia
            ? item.name === selectedTipologia
                ? { opacity: 1, shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' }
                : { opacity: 0.3 }
            : {}
    }));

    // Il grafico a barre mostra solo i modelli della tipologia selezionata (se presente)
    const modelliData = selectedTipologia
        ? modelliDataFromApi.filter(item => item.tipologia === selectedTipologia)
        : modelliDataFromApi;

    const MESI_NOMI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];

    // il backend restituisce già solo i mesi nel range, con l'anno
    let labelsMensili = [];
    let targheTotaliMensili = [];
    let targheCircolantiMensili = [];

    for (let i = 0; i < comboDataMensileFromApi.length; i++) {
        const nomeMese = MESI_NOMI[comboDataMensileFromApi[i].mese - 1];
        const anno = comboDataMensileFromApi[i].anno;
        labelsMensili.push(nomeMese + ' ' + anno);
        targheTotaliMensili.push(Number(comboDataMensileFromApi[i].targhe_totali));
        targheCircolantiMensili.push(Number(comboDataMensileFromApi[i].targhe_circolanti));
    }

    const circolantiDataMensile = {
        labels: labelsMensili,
        targheTotali: targheTotaliMensili,
        targheCircolanti: targheCircolantiMensili,
    };

    // Preparo i dati generali per il grafico combo (tutti i veicoli)
    const circolantiDataTrimestre = {
        labels: comboDataTrimestraleFromApi.map(item => item.label),
        targheTotali: comboDataTrimestraleFromApi.map(item => Number(item.targhe_totali)),
        targheCircolanti: comboDataTrimestraleFromApi.map(item => Number(item.targhe_circolanti)),
    };


    const circolantiDataAnno = {
        labels: comboDataAnnualeFromApi.map(item => String(item.anno)),
        targheTotali: comboDataAnnualeFromApi.map(item => Number(item.targhe_totali)),
        targheCircolanti: comboDataAnnualeFromApi.map(item => Number(item.targhe_circolanti)),
    };

    // Dati mensili filtrati per tipologia (il backend restituisce già solo i mesi nel range)
    const circolantiDataMensileFiltered = {
        targheTotali: comboDataMensileFiltered.map(item => Number(item.targhe_totali)),
        targheCircolanti: comboDataMensileFiltered.map(item => Number(item.targhe_circolanti)),
    };

    const circolantiDataTrimestreFiltered = {
        targheTotali: comboDataTrimestraleFiltered.map(item => Number(item.targhe_totali)),
        targheCircolanti: comboDataTrimestraleFiltered.map(item => Number(item.targhe_circolanti)),
    };

    const circolantiDataAnnoFiltered = {
        targheTotali: comboDataAnnualeFiltered.map(item => Number(item.targhe_totali)),
        targheCircolanti: comboDataAnnualeFiltered.map(item => Number(item.targhe_circolanti)),
    };

    const getComboChartData = () => {
        if (comboViewType === 'trimestre') {
            return {
                labels: circolantiDataTrimestre.labels,
                targheTotali: circolantiDataTrimestre.targheTotali,
                targheCircolanti: circolantiDataTrimestre.targheCircolanti,
                // dati filtrati (vuoti se nessuna selezione)
                targheTotaliFiltered: circolantiDataTrimestreFiltered.targheTotali,
                targheCircolantiFiltered: circolantiDataTrimestreFiltered.targheCircolanti,
            };
        } else if (comboViewType === 'anno') {
            return {
                labels: circolantiDataAnno.labels,
                targheTotali: circolantiDataAnno.targheTotali,
                targheCircolanti: circolantiDataAnno.targheCircolanti,
                targheTotaliFiltered: circolantiDataAnnoFiltered.targheTotali,
                targheCircolantiFiltered: circolantiDataAnnoFiltered.targheCircolanti,
            };
        } else {
            // vista mensile
            return {
                labels: circolantiDataMensile.labels,
                targheTotali: circolantiDataMensile.targheTotali,
                targheCircolanti: circolantiDataMensile.targheCircolanti,
                targheTotaliFiltered: circolantiDataMensileFiltered.targheTotali,
                targheCircolantiFiltered: circolantiDataMensileFiltered.targheCircolanti,
            };
        }
    };

    const comboData = getComboChartData();
    const hasFilteredData = selectedTipologia && comboData.targheTotaliFiltered?.length > 0;

    // Numero totale di veicoli della tipologia selezionata (serve per calcolare la percentuale)
    const totaleTipologiaSelezionata = selectedTipologia
        ? tipologieDataFromApi.find(t => t.name === selectedTipologia)?.value || 0
        : 0;

    // palette moderna con gradienti verticali per le fette della torta
    const MODERN_PIE_GRADIENTS = [
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#60A5FA' }, { offset: 1, color: '#2563EB' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#A78BFA' }, { offset: 1, color: '#7C3AED' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#F472B6' }, { offset: 1, color: '#DB2777' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#FBBF24' }, { offset: 1, color: '#D97706' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#34D399' }, { offset: 1, color: '#059669' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#22D3EE' }, { offset: 1, color: '#0891B2' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#818CF8' }, { offset: 1, color: '#4F46E5' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#FB923C' }, { offset: 1, color: '#EA580C' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#2DD4BF' }, { offset: 1, color: '#0D9488' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#C084FC' }, { offset: 1, color: '#9333EA' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#F87171' }, { offset: 1, color: '#DC2626' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#A3E635' }, { offset: 1, color: '#65A30D' }] }
    ];

    // totale veicoli e testo centrale del donut (cambia in base alla selezione)
    const totaleVeicoli = tipologieDataFromApi.reduce((sum, t) => sum + Number(t.value || 0), 0);
    const centerMainText = selectedTipologia ? String(totaleTipologiaSelezionata) : String(totaleVeicoli);
    const centerSubText = selectedTipologia
        ? `${selectedTipologia} · ${totaleVeicoli > 0 ? ((totaleTipologiaSelezionata / totaleVeicoli) * 100).toFixed(1) : 0}%`
        : 'Totale veicoli';

    // configurazione grafico a torta (donut moderno con gradienti)
    const pieChartOption = {
        color: MODERN_PIE_GRADIENTS,
        title: { show: false },
        tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            borderColor: 'transparent',
            borderRadius: 10,
            padding: [10, 14],
            textStyle: { color: '#F8FAFC', fontSize: 12, fontWeight: 500 },
            extraCssText: 'box-shadow: 0 10px 25px rgba(0,0,0,0.25); backdrop-filter: blur(8px);',
            formatter: (params) => `
                <div style="font-weight:700;font-size:13px;margin-bottom:4px;">${params.name}</div>
                <div style="display:flex;align-items:center;gap:6px;">
                    <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${typeof params.color === 'string' ? params.color : (params.color?.colorStops?.[0]?.color || '#3B82F6')};"></span>
                    <span>${params.value} veicoli · ${params.percent}%</span>
                </div>
            `
        },
        legend: {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 0,
            left: 'center',
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 12,
            icon: 'circle',
            textStyle: { fontSize: 11, color: '#475569', fontWeight: 500 },
            pageIconColor: '#3B82F6',
            pageTextStyle: { color: '#64748B' }
        },
        graphic: [
            {
                type: 'text',
                left: 'center',
                top: '38%',
                z: 10,
                style: {
                    text: centerMainText,
                    fontSize: 30,
                    fontWeight: 700,
                    fill: '#0F172A',
                    textAlign: 'center',
                    textVerticalAlign: 'middle',
                    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
                }
            },
            {
                type: 'text',
                left: 'center',
                top: '50%',
                z: 10,
                style: {
                    text: centerSubText,
                    fontSize: 11,
                    fontWeight: 500,
                    fill: '#64748B',
                    textAlign: 'center',
                    textVerticalAlign: 'middle',
                    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
                }
            }
        ],
        series: [
            {
                name: 'Tipologia',
                type: 'pie',
                radius: ['55%', '78%'],
                center: ['50%', '45%'],
                avoidLabelOverlap: true,
                padAngle: 2,
                itemStyle: {
                    borderRadius: 8,
                    borderColor: '#ffffff',
                    borderWidth: 3,
                    shadowBlur: 10,
                    shadowColor: 'rgba(15, 23, 42, 0.12)'
                },
                label: {
                    show: true,
                    position: 'outside',
                    formatter: '{b|{b}}\n{c|{c}} · {p|{d}%}',
                    rich: {
                        b: { color: '#1E293B', fontSize: 11, fontWeight: 600, lineHeight: 16 },
                        c: { color: '#475569', fontSize: 11, fontWeight: 500, lineHeight: 14 },
                        p: { color: '#3B82F6', fontSize: 11, fontWeight: 700, lineHeight: 14 }
                    }
                },
                labelLine: {
                    show: true,
                    length: 10,
                    length2: 12,
                    smooth: true,
                    lineStyle: { color: '#CBD5E1', width: 1 }
                },
                emphasis: {
                    scale: true,
                    scaleSize: 12,
                    itemStyle: {
                        shadowBlur: 25,
                        shadowColor: 'rgba(59, 130, 246, 0.45)'
                    },
                    label: { fontWeight: 700 }
                },
                data: tipologieData,
                animationType: 'scale',
                animationEasing: 'elasticOut',
                animationDuration: 900,
                animationDelay: (idx) => idx * 60
            }
        ]
    };

    // handler per click sulla torta
    const handlePieClick = (params) => {
        if (params.name === selectedTipologia) {
            // deseleziono se già cliccata
            setSelectedTipologia(null);
        } else {
            setSelectedTipologia(params.name);
        }
    };

    // configurazione grafico a barre orizzontali
    const barChartOption = {
        title: {
            show: false
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' }
        },
        grid: {
            left: '3%',
            right: '10%',
            bottom: '3%',
            top: '2%',
            containLabel: true
        },
        xAxis: {
            type: 'value'
        },
        yAxis: {
            type: 'category',
            data: modelliData.map(item => item.name),
            inverse: true
        },
        series: [
            {
                name: 'Veicoli',
                type: 'bar',
                data: modelliData.map(item => item.value),
                itemStyle: {
                    color: '#2E6DB1'
                },
                label: {
                    show: true,
                    position: 'right'
                }
            }
        ]
    };

    // handler per click sul grafico a barre
    const handleBarclick = (params) => {
        // uso dataIndex per trovare il modello cliccato nell'array filtrato
        const modelloCliccato = modelliData[params.dataIndex];

        if (!modelloCliccato) {
            return;
        }

        if (modelloCliccato.tipologia === selectedTipologia) {
            setSelectedTipologia(null);
        } else {
            setSelectedTipologia(modelloCliccato.tipologia)
        }
    }

    // preparo i dati per le barre sovrapposte
    const getStackedBarData = () => {
        if (!hasFilteredData) {
            // Nessun filtro: mostra tutti i dati
            return {
                totaliFiltered: comboData.targheTotali,
                totaliResto: comboData.targheTotali.map(() => 0),
                circolantiFiltered: comboData.targheCircolanti,
                circolantiResto: comboData.targheCircolanti.map(() => 0),
            };
        }
        // Con filtro attivo: mostra i dati filtrati
        return {
            totaliFiltered: comboData.targheTotaliFiltered,
            totaliResto: comboData.targheTotaliFiltered.map(() => 0), // sempre 0, no parte sfumata
            circolantiFiltered: comboData.targheCircolantiFiltered,
            circolantiResto: comboData.targheCircolantiFiltered.map(() => 0), // sempre 0, no parte sfumata
        };
    };

    const stackedData = getStackedBarData();

    // configurazione grafico combo
    const comboChartOption = {
        title: {
            // text: 'Numero di veicoli circolanti',
            left: 'left',
            top: '-5px'
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: (params) => {
                let result = params[0].axisValue + '<br/>';
                // calcolo i totali per il tooltip
                const totaliValue = (stackedData.totaliFiltered[params[0].dataIndex] || 0) +
                    (stackedData.totaliResto[params[0].dataIndex] || 0);
                const circolantiValue = (stackedData.circolantiFiltered[params[0].dataIndex] || 0) +
                    (stackedData.circolantiResto[params[0].dataIndex] || 0);

                result += `<span style="color:#5470c6">●</span> Targhe Totali: ${totaliValue}<br/>`;
                result += `<span style="color:#91cc75">●</span> Targhe Circolanti: ${circolantiValue}<br/>`;

                if (hasFilteredData) {
                    result += `<span style="color:#5470c6">●</span> ${selectedTipologia} (Totali): ${stackedData.totaliFiltered[params[0].dataIndex] || 0}<br/>`;
                    result += `<span style="color:#91cc75">●</span> ${selectedTipologia} (Circolanti): ${stackedData.circolantiFiltered[params[0].dataIndex] || 0}<br/>`;
                }

                // aggiungo la percentuale
                const lineParam = params.find(p => p.seriesName === '% veicoli circolanti');
                if (lineParam) {
                    result += `<span style="color:#ee6666">●</span> % Circolanti: ${lineParam.value}%`;
                }
                return result;
            }
        },
        legend: {
            data: ['Targhe Totali', 'Targhe Circolanti', '% veicoli circolanti'],
            top: 0,
            left: 0
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: comboData.labels,
            axisLabel: {
                rotate: 45
            }
        },
        yAxis: [
            {
                type: 'value',
                name: 'Veicoli',
                position: 'left',
            },
            {
                type: 'value',
                name: '%',
                position: 'right',
                min: 0,
                // max: 200,
                axisLabel: {
                    formatter: '{value}%'
                }
            }
        ],
        series: [
            // barra blu - targhe totali (o parte evidenziata se filtro attivo)
            {
                name: 'Targhe Totali',
                type: 'bar',
                stack: 'totali',
                barGap: '0%',
                data: stackedData.totaliFiltered,
                itemStyle: {
                    color: '#5470c6',
                    borderColor: '#5470c6',
                    borderWidth: 1
                },
                emphasis: { disabled: true },
                // mostro sempre il valore dentro la barra (anche con filtro attivo)
                label: {
                    show: true,
                    position: 'inside',
                    formatter: '{c}',
                    color: '#fff',
                    fontWeight: 'bold'
                }
            },
            // barra verde - targhe circolanti (o parte evidenziata se filtro attivo)
            {
                name: 'Targhe Circolanti',
                type: 'bar',
                stack: 'circolanti',
                data: stackedData.circolantiFiltered,
                itemStyle: {
                    color: '#91cc75',
                    borderColor: '#91cc75',
                    borderWidth: 1
                },
                emphasis: { disabled: true },
                // mostro sempre il valore dentro la barra (anche con filtro attivo)
                label: {
                    show: true,
                    position: 'inside',
                    formatter: '{c}',
                    color: '#fff',
                    fontWeight: 'bold'
                }
            },
            // linea rossa con la percentuale
            {
                name: '% veicoli circolanti',
                type: 'line',
                yAxisIndex: 1,
                data: hasFilteredData
                    ? comboData.targheTotaliFiltered.map((tot, i) =>
                        tot > 0 ? parseFloat(((comboData.targheCircolantiFiltered[i] / tot) * 100).toFixed(2)) : 0
                    )
                    : comboData.targheTotali.map((tot, i) =>
                        tot > 0 ? parseFloat(((comboData.targheCircolanti[i] / tot) * 100).toFixed(2)) : 0
                    ),
                itemStyle: { color: '#ee6666' },
                lineStyle: { width: 4 },
                symbolSize: 10,
                label: {
                    show: true,
                    position: 'top',
                    formatter: '{c}%'
                }
            }
        ]
    };

    // Configurazione istogramma anzianità veicoli
    const anzianitaChartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const p = params[0];
                const idx = p.dataIndex;
                const item = anzianitaData[idx];
                let result = `<strong>${p.name} ann${p.name === '1' ? 'o' : 'i'}</strong> — ${p.value} veicoli<br/>`;
                if (item && item.sottotipologie) {
                    const sorted = [...item.sottotipologie].sort((a, b) => b.num_veicoli - a.num_veicoli);
                    for (const s of sorted) {
                        result += `<span style="color:#64748b">▸</span> ${s.sottotipologia || 'N/D'}: <strong>${s.num_veicoli}</strong><br/>`;
                    }
                }
                return result;
            }
        },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
            type: 'category',
            data: anzianitaData.map(d => String(d.anni)),
            name: 'Anni',
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: { fontSize: 11 }
        },
        yAxis: {
            type: 'value',
            name: 'N. Veicoli',
            nameLocation: 'end',
            nameGap: 15,
            minInterval: 1
        },
        series: [{
            type: 'bar',
            data: anzianitaData.map(d => d.num_veicoli),
            itemStyle: {
                color: (params) => {
                    const anni = anzianitaData[params.dataIndex]?.anni;
                    if (anni <= 3) return '#10B981';
                    if (anni <= 6) return '#F59E0B';
                    return '#EF4444';
                },
                borderRadius: [4, 4, 0, 0]
            },
            label: {
                show: true,
                position: 'top',
                fontWeight: 'bold',
                fontSize: 11
            },
            barMaxWidth: 50
        }]
    };

    // Configurazione grafico anzianità media per sottotipologia (barre orizzontali)
    const anzianitaMediaChartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const p = params[0];
                const item = anzianitaMediaData[anzianitaMediaData.length - 1 - p.dataIndex];
                return `<strong>${p.name}</strong><br/>Anzianità media: <strong>${p.value} anni</strong><br/>Veicoli attivi: <strong>${item?.num_veicoli || 0}</strong>`;
            }
        },
        grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
        xAxis: {
            type: 'value',
            name: 'Anni',
            nameLocation: 'end',
            nameGap: 5
        },
        yAxis: {
            type: 'category',
            data: [...anzianitaMediaData].reverse().map(d => d.sottotipologia || 'N/D'),
            axisLabel: { fontSize: 10, width: 140, overflow: 'truncate' }
        },
        series: [{
            type: 'bar',
            data: [...anzianitaMediaData].reverse().map(d => Number(d.anzianita_media)),
            itemStyle: {
                color: (params) => {
                    const val = params.value;
                    if (val <= 2) return '#10B981';
                    if (val <= 5) return '#F59E0B';
                    return '#EF4444';
                },
                borderRadius: [0, 4, 4, 0]
            },
            label: {
                show: true,
                position: 'right',
                formatter: '{c}',
                fontSize: 10
            },
            barMaxWidth: 25
        }]
    };

    return (
        <div className="generale-charts-container">
            {isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="oper-hr-empty-state">
                    <div className="oper-hr-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                    <p className="oper-hr-empty-text">
                        Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.
                    </p>
                </div>
            ) : isDataReady ? (
                <>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    {/* grafico a torta */}
                    <div className={`chart-wrapper${isFullscreenPie ? ' fullscreen-chart' : ''}`} style={{ flex: '1 1 400px', minWidth: 0, flexDirection: 'column' }}>
                        <RotateDevice active={isFullscreenPie} />
                        <div className="automezzi-gen-chart-header">
                            <span className="charts-title">Numero di veicoli per tipologia</span>
                            <button
                                className="automezzi-gen-fullscreen-btn"
                                onClick={() => setIsFullscreenPie(!isFullscreenPie)}
                                title={isFullscreenPie ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenPie ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        <ReactECharts
                            option={pieChartOption}
                            style={{ height: '400px', width: '100%' }}
                            onEvents={{ click: handlePieClick }}

                        />
                    </div>

                    {/* grafico a barre orizzontali */}
                    <div className={`chart-wrapper${isFullscreenModelli ? ' fullscreen-chart' : ''}`} style={{ flex: '1 1 400px', minWidth: 0, flexDirection: 'column' }}>
                        <RotateDevice active={isFullscreenModelli} />
                        <div className="automezzi-gen-chart-header">
                            <span className="charts-title">Numero veicoli per modello</span>
                            <button
                                className="automezzi-gen-fullscreen-btn"
                                onClick={() => setIsFullscreenModelli(!isFullscreenModelli)}
                                title={isFullscreenModelli ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenModelli ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        <ReactECharts
                            option={barChartOption}
                            style={{ height: '400px', width: '100%' }}
                            onEvents={{ click: handleBarclick }}
                        />
                    </div>
                </div>

                {/* grafico combo */}
                <div className={`automezzi-gen-combo-wrapper${isFullscreenCombo ? ' fullscreen-chart' : ''}`} style={{ marginTop: '20px' }}>
                    <RotateDevice active={isFullscreenCombo} />
                    <div className="automezzi-gen-combo-header automezzi-gen-chart-header">
                        <span className="charts-title">Numero di veicoli circolanti e relativi costi</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="oper-hr-table-view-type-container">
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${comboViewType === 'mese' ? 'active' : ''}`}
                                    onClick={() => setComboViewType('mese')}
                                    title="Vista Mensile"
                                >
                                    <span>M</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${comboViewType === 'trimestre' ? 'active' : ''}`}
                                    onClick={() => setComboViewType('trimestre')}
                                    title="Vista Trimestrale"
                                >
                                    <span>T</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${comboViewType === 'anno' ? 'active' : ''}`}
                                    onClick={() => setComboViewType('anno')}
                                    title="Vista Annuale"
                                >
                                    <span>A</span>
                                </button>
                            </div>
                            <button
                                className="automezzi-gen-fullscreen-btn"
                                onClick={() => setIsFullscreenCombo(!isFullscreenCombo)}
                                title={isFullscreenCombo ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenCombo ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="chart-wrapper">
                        <ReactECharts
                            option={comboChartOption}
                            style={{ height: '400px', width: '100%' }}
                            notMerge={true}
                        />
                    </div>
                </div>

                {/* Sezione istogrammi anzianità */}
                {anzianitaData.length > 0 && (() => {
                    const anzianitaSharedHeight = `${Math.max(400, anzianitaMediaData.length * 28)}px`;
                    return (
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
                        {/* Istogramma anzianità veicoli */}
                        <div className={`chart-wrapper${isFullscreenAnzianita ? ' fullscreen-chart' : ''}`} style={{ flex: '1 1 400px', minWidth: 0, flexDirection: 'column' }}>
                            <RotateDevice active={isFullscreenAnzianita} />
                            <div className="automezzi-gen-chart-header">
                                <span className="charts-title">Anzianità veicoli attivi (anni)</span>
                                <button
                                    className="automezzi-gen-fullscreen-btn"
                                    onClick={() => setIsFullscreenAnzianita(!isFullscreenAnzianita)}
                                    title={isFullscreenAnzianita ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenAnzianita ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <ReactECharts
                                option={anzianitaChartOption}
                                style={{ height: anzianitaSharedHeight, width: '100%' }}
                            />
                        </div>

                        {/* Istogramma anzianità media per sottotipologia */}
                        <div className={`chart-wrapper${isFullscreenAnzianitaMedia ? ' fullscreen-chart' : ''}`} style={{ flex: '1 1 400px', minWidth: 0, flexDirection: 'column' }}>
                            <RotateDevice active={isFullscreenAnzianitaMedia} />
                            <div className="automezzi-gen-chart-header">
                                <span className="charts-title">Anzianità media per sottotipologia (anni)</span>
                                <button
                                    className="automezzi-gen-fullscreen-btn"
                                    onClick={() => setIsFullscreenAnzianitaMedia(!isFullscreenAnzianitaMedia)}
                                    title={isFullscreenAnzianitaMedia ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenAnzianitaMedia ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <ReactECharts
                                option={anzianitaMediaChartOption}
                                style={{ height: anzianitaSharedHeight, width: '100%' }}
                            />
                        </div>
                    </div>
                    );
                })()}

                {/* Tabella km per anno di immatricolazione */}
                <KmPerImmatricolazione filterParams={buildFilterParams()} />
                </>
            ) : null}
        </div>
    );
};

export default AutomezziCharts;

